/**
 * `start_pb` MCP tool — universal session opener implementing PAT-227.
 *
 * WP-433 S3 (FEAT-1296) — collapses `start` + `start_pb` into ONE tool.
 *
 * Canonical name decision: keeping `start_pb` (Option A, DEC supersedes DEC-997).
 *   Rationale: user trigger is literally "Start PB" — `start_pb` is the closest
 *   natural match. Avoids renaming cascade across rules/docs. "start" alone is
 *   too generic and may attract unrelated intents. See proposed DEC in report.
 *
 * Stage-branched delivery (PAT-227):
 *   blank / seeded  → SKILL-pb-setup body verbatim via loadSkillBody (DEC-1016 SSOT)
 *   grounded / connected → orient-style standup briefing from start.ts buildOrientResponse
 *   readiness failure → orient-style (safest degraded path — never show setup on active ws)
 *
 * TEN-2028: surfaceCapability self-declaration removed from recordSetupInvocation call.
 *   The mutation requires surfaceCapability as a required arg; removing the call entirely
 *   (it was best-effort / wrapped in try-catch) is the correct approach per TEN-2028.
 *   stampMcpOnlySurface is preserved for blank/seeded workspaces only (DEC-993 / STD-235).
 */

import { z } from "zod/v3";
import {
  kernelQuery,
  kernelMutation,
  kernelCall,
  getWorkspaceContext,
  getAgentSessionId,
  getConversationId,
  setSessionOriented,
} from "../client.js";
import { success, failure, textContent, type NextAction } from "../envelope.js";
import { loadSkillBody, type SkillBodyResult } from "./skills.js";
import { queryPlannedWork, hasPlannedWork, buildPlannedWorkSection, formatRecoveryBlock, type PriorSession } from "./planned-work.js";
import { buildOperatingProtocol, buildCoherenceSection, buildDocCompletenessSection, type ActiveBet, type CoherenceSnapshot, type StandardEntry, type CollectionDocFields, type GovernanceEntries } from "./orient-shared.js";
import { getTopGaps } from "../gap-store.js";
import { formatTopGapPrompt, formatGapList, type ReadinessGap, type GapContext } from "../lib/gapToPrompt.js";

export const startPbSchema = z.object({
  task: z
    .string()
    .max(2000)
    .optional()
    .describe(
      "What you're about to work on (e.g. 'implementing auth middleware'). " +
      "Grounded/connected workspaces: filters governance to show relevant principles, standards, and business rules. " +
      "Blank/seeded workspaces: ignored (setup flow takes over).",
    ),
  // TEN-2431: no `scope` param. start_pb's governance matches orient's RENDERED governance,
  // which is scope-BLIND (orient never renders the domain-scoped startup.bindingGovernance).
  // Domain-scoping the rendered surface is deferred to TEN-2429 (it must change BOTH orient
  // and start_pb together; INS-1864), so exposing a `scope` here would imply a scoping
  // capability that does not exist and would diverge start_pb from orient.
});

const PB_SETUP_SKILL_ENTRY_ID = "SKILL-pb-setup";

/**
 * TEN-1740: structural mirror of planned-work.ts's `formatRecoveryBlock` parameter shape
 * (itself a mirror of convex's OrientRecoveryBlock; also mirrored in orient.ts's
 * `RecoveryBlockData`). Declared once here so every call site in this file shares one type
 * instead of a per-call-site `as Parameters<...>` cast.
 */
type RecoveryBlockData = {
  sessionId: string;
  status: "timed_out" | "completed";
  date: string;
  duration: string;
  created: { entryId: string; collection: string; name: string }[];
  modified: { entryId: string; collection: string; name: string }[];
  draftsNeedingAttention: { entryId: string; collection: string; name: string }[];
  relationsCreated: number;
  createdOverflow?: number;
  modifiedOverflow?: number;
  draftsOverflow?: number;
};

/** Shape returned by chain.workspaceReadiness — local mirror of Convex type. */
interface WorkspaceReadiness {
  score: number;
  totalChecks: number;
  passedChecks: number;
  checks: Array<{ passed: boolean; label: string; current: number; required: number }>;
  gaps: ReadinessGap[];
  stats: {
    totalEntries: number;
    activeCount: number;
    draftCount: number;
    totalRelations: number;
    collectionCount: number;
    orphanedCount: number;
  };
  stage: "blank" | "seeded" | "grounded" | "connected";
  scoringVersion: "v1" | "v2";
  governanceMode?: string;
}

async function tryMarkOriented(
  agentSessionId: string | null,
  coherenceSnapshot?: CoherenceSnapshot,
): Promise<{
  oriented: boolean;
  orientationStatus: "complete" | "failed" | "no_session";
}> {
  if (!agentSessionId) return { oriented: false, orientationStatus: "no_session" };
  try {
    await kernelCall("agent.markOriented", {
      sessionId: agentSessionId,
      ...(coherenceSnapshot ? { coherenceSnapshot } : {}),
    });
    setSessionOriented(true);
    return { oriented: true, orientationStatus: "complete" };
  } catch {
    if (!coherenceSnapshot) {
      return { oriented: false, orientationStatus: "failed" };
    }
    try {
      await kernelCall("agent.markOriented", { sessionId: agentSessionId });
      setSessionOriented(true);
      return { oriented: true, orientationStatus: "complete" };
    } catch {
      return { oriented: false, orientationStatus: "failed" };
    }
  }
}

/** TEN-547: Bets collection filter — status at top-level or in data. */
function isActiveNowBet(e: { status?: string; data?: Record<string, unknown> }): boolean {
  const status = e.status ?? (e.data as { status?: string } | undefined)?.status;
  const horizon = (e.data as { horizon?: string } | undefined)?.horizon;
  return status === "active" && horizon === "now";
}

function computeWorkspaceAge(createdAt: number | null): { ageDays: number; isNeglected: boolean } {
  if (!createdAt) return { ageDays: 0, isNeglected: false };
  const ageDays = Math.floor((Date.now() - createdAt) / (1000 * 60 * 60 * 24));
  return { ageDays, isNeglected: ageDays >= 30 };
}

function pickNextTensionPrompt(
  openTensions: Array<{ name: string }>,
): { action: string; cta: string } | null {
  if (openTensions.length === 0) return null;
  const t = openTensions[0];
  return {
    action: `Open tension: ${t.name}`,
    cta: "Want to discuss this tension or capture a decision about it?",
  };
}

/**
 * Blank/seeded stage: return SKILL-pb-setup body verbatim (DEC-1016 SSOT).
 * Also stamps the MCP-only surface for chat-only callers (DEC-993 / STD-235).
 *
 * TEN-2028: recordSetupInvocation call removed — it required surfaceCapability
 * as a required argument, which would constitute self-declaration of surface
 * capability. The invocation receipt can be recorded by the CLI path or a
 * dedicated record_activation call post-onboarding.
 */
async function buildSetupResponse(
  agentSessionId: string | null,
): Promise<{ text: string; structuredContent: Record<string, unknown> }> {
  // DEC-993 / STD-235: stamp manifest.surfaces with mcp-only (forward-only union)
  await kernelMutation<{ stamped: boolean; surfaces: string[] }>(
    "setup.stampMcpOnlySurface",
    {},
  );

  const skill: SkillBodyResult = await loadSkillBody(PB_SETUP_SKILL_ENTRY_ID);

  const { oriented, orientationStatus } = await tryMarkOriented(agentSessionId);

  return {
    text: skill.body,
    structuredContent: success(
      `pb-setup ready. Delivering SKILL-pb-setup body (${skill.body.length} chars).`,
      {
        bootstrap: "pb-setup",
        skillEntryId: skill.entryId,
        skillBody: skill.body,
        stage: "setup",
        oriented,
        orientationStatus,
        ...(agentSessionId ? { sessionId: agentSessionId } : {}),
        instructions: "Read skillBody and follow it; use entries/orient MCP tools per PAT-227.",
      },
    ),
  };
}

/**
 * Grounded/connected stage: return orient-style standup briefing.
 * Mirrors buildOrientResponse from the former start.ts.
 */
/**
 * TEN-2431: rendered-governance row shape returned by chain.getBindingGovernanceView.
 * These are orient's top-level display rows (makeOrientToDisplay) — `tier` rides each
 * entry (DEC-446 tiered ranker); `preview` is the description/WHY.
 */
type BindingGovernanceEntry = { entryId?: string | null; name: string; preview?: string; tier?: number };
type BindingGovernanceView = {
  bindingGovernance?: {
    principles?: BindingGovernanceEntry[];
    standards?: BindingGovernanceEntry[];
    businessRules?: BindingGovernanceEntry[];
  };
};

async function buildOrientResponse(
  wsCtx: { workspaceName: string; workspaceSlug: string; workspaceId: string; createdAt?: number | null; governanceMode?: string },
  agentSessionId: string | null,
  errors: string[],
  task?: string,
): Promise<{ text: string; structuredContent: Record<string, unknown> }> {
  const { ageDays, isNeglected } = computeWorkspaceAge(wsCtx.createdAt ?? null);

  let priorSessions: PriorSession[] = [];
  let recoveryBlock: RecoveryBlockData | null = null;
  try {
    // WP-479 review fix (Codex): scope recovery to THIS conversation when one resolves. Once N
    // conversations share one API key, a bare `{ limit: 3 }` surfaces another concurrent
    // conversation's closed sessions/drafts for the same key — the cross-talk the server-side
    // conversationId filter (agentSessions.getRecentClosedSessions) exists to prevent. Thread the
    // MCP conversation identity so the filter engages; absent identity ⇒ legacy shared behavior.
    const recentConversationId = getConversationId();
    const sessionsResult = await kernelQuery<{ sessions: PriorSession[]; recoveryBlock: RecoveryBlockData | null }>(
      "agent.recentSessions",
      recentConversationId ? { limit: 3, conversationId: recentConversationId } : { limit: 3 },
    );
    priorSessions = sessionsResult?.sessions ?? [];
    recoveryBlock = sessionsResult?.recoveryBlock ?? null;
  } catch { /* non-critical */ }

  let openTensions: Array<{ name: string; entryId?: string }> = [];
  try {
    const tensions = await kernelQuery<Array<{ name: string; entryId?: string; workflowStatus?: string }>>("chain.listEntries", { collectionSlug: "tensions" });
    openTensions = (tensions ?? []).filter((e) => e.workflowStatus === "open");
  } catch { /* non-critical */ }

  let readiness: WorkspaceReadiness | null = null;
  try {
    readiness = await kernelQuery<WorkspaceReadiness>("chain.workspaceReadiness");
  } catch (e: unknown) {
    errors.push(`Readiness: ${e instanceof Error ? e.message : String(e)}`);
  }

  const lines: string[] = [];
  const isLowReadiness = readiness !== null && readiness.score < 50;
  const isHighReadiness = readiness !== null && readiness.score >= 50;
  const stage: string | null = readiness?.stage ?? null;
  const captureBehaviorNote =
    wsCtx.governanceMode === "open"
      ? "_In Open mode, user-authored captures are accepted immediately unless you ask me to keep them as drafts._"
      : "_Everything I capture stays pending until you confirm._";

  const coherence = buildCoherenceSection();

  let allCollections: CollectionDocFields[] = [];
  try {
    allCollections = (await kernelQuery<CollectionDocFields[]>("chain.listCollections")) ?? [];
  } catch { /* non-critical */ }

  lines.push(`# ${wsCtx.workspaceName}`);
  lines.push("_Product Brain is ready._");
  lines.push("");

  if (isLowReadiness && isNeglected) {
    const stageNote = stage ? ` and is still at the **${stage}** stage` : "";
    lines.push(`Your workspace has been around for ${ageDays} days${stageNote}.`);
    lines.push("Let's close the gaps — or if the current structure doesn't fit, we can reshape it.");
    lines.push("");
  } else if (isLowReadiness) {
    lines.push("Let's get your workspace active.");
    lines.push("");
  }

  if (isLowReadiness && readiness) {
    const gaps: ReadinessGap[] = readiness.gaps ?? [];
    if (gaps.length > 0) {
      const gapCtx: GapContext = {
        stage: stage ?? "seeded",
        passedChecks: readiness.passedChecks ?? 0,
        totalChecks: readiness.totalChecks ?? 0,
        score: readiness.score ?? 0,
      };

      lines.push(formatTopGapPrompt(gaps[0], gaps.length - 1, gapCtx));
      lines.push("");
      lines.push(captureBehaviorNote);
      lines.push("");

      const remainingGaps = gaps.length - 1;
      if (remainingGaps > 0 || openTensions.length > 0) {
        const parts: string[] = [];
        if (remainingGaps > 0) parts.push(`${remainingGaps} more area${remainingGaps === 1 ? "" : "s"} to cover`);
        if (openTensions.length > 0) parts.push(`${openTensions.length} open tension${openTensions.length === 1 ? "" : "s"}`);
        lines.push(`_${parts.join(" and ")} — ask for full status to see everything._`);
        lines.push("");
      }
    } else {
      const tensionPrompt = pickNextTensionPrompt(openTensions);
      if (tensionPrompt) {
        lines.push(`**${tensionPrompt.action}**`);
        lines.push(tensionPrompt.cta);
        lines.push("");
      }
    }

    lines.push("_Need a collection that doesn't exist yet? Just ask — I'll set it up._");
    lines.push("");
  } else if (isHighReadiness) {
    let activeBets: ActiveBet[] = [];
    try {
      const betsEntries = await kernelQuery<Array<ActiveBet & { data?: { horizon?: string; status?: string }; status?: string }>>("chain.listEntries", { collectionSlug: "work-packages" });
      activeBets = (betsEntries ?? [])
        .filter((e) => isActiveNowBet(e))
        .map((e) => ({ name: e.name, entryId: e.entryId ?? null }))
        .slice(0, 8);
    } catch { /* non-critical */ }

    // TEN-2431: route governance through the SHARED DEC-446 tiered ranker via the backend
    // (chain.getBindingGovernanceView), instead of the old hand-rolled chain.listEntries loop
    // (which rendered in listing order). This serves start_pb the SAME governance orient
    // RENDERS for the same task — scope-BLIND and tiered. Domain-scoping is deliberately
    // DEFERRED to TEN-2429 (it must change BOTH orient's and start_pb's rendered paths together;
    // INS-1864), so there is NO scope arg here — start_pb matches orient's current scope-blind
    // rendered governance. Only fetched on the task path: orient's no-task path renders the
    // compact protocol with NO governance, so start_pb must not load it when no task is present.
    // Non-fatal: a governance-load failure must NOT break the universal session opener (start_pb
    // is the first call), so empty buckets fall through to buildOperatingProtocol's compact mode.
    let governanceEntries: GovernanceEntries = { principles: [], standards: [], businessRules: [] };
    if (task) {
      try {
        const view = await kernelQuery<BindingGovernanceView>("chain.getBindingGovernanceView", { task });
        const bg = view?.bindingGovernance;
        if (bg) {
          // Map backend rendered governance rows → buildOperatingProtocol's entry shape. The rows
          // are orient's top-level display rows (makeOrientToDisplay): `preview` is the description,
          // `tier` is threaded so the shared ranker's ordering is preserved (buildOperatingProtocol
          // tier-sorts when any entry has a tier).
          const mapGovEntry = (e: BindingGovernanceEntry): StandardEntry => ({
            entryId: e.entryId ?? undefined,
            name: e.name,
            description: typeof e.preview === "string" ? e.preview : undefined,
            tier: typeof e.tier === "number" ? e.tier : undefined,
          });
          governanceEntries = {
            principles: (bg.principles ?? []).map(mapGovEntry),
            standards: (bg.standards ?? []).map(mapGovEntry),
            businessRules: (bg.businessRules ?? []).map(mapGovEntry),
          };
        }
      } catch { /* non-critical */ }
    }

    if (task) {
      if (activeBets.length > 0) {
        lines.push("");
        lines.push("## Active bets — current scope");
        lines.push("_Work outside these bets requires explicit user confirmation._");
        lines.push("");
        for (const e of activeBets) {
          lines.push(`- \`${e.entryId ?? e.name}\` ${e.name}`);
        }
        lines.push("");
      }

      // TEN-2431: governance now comes from the shared tiered + domain-scoped backend view
      // (governanceEntries, fetched above), NOT a hand-rolled per-slug listEntries loop.
      lines.push(...buildOperatingProtocol(governanceEntries, task));

      if (coherence) {
        lines.push(...coherence.lines);
      }

      if (allCollections.length > 0) {
        const docCompleteness = buildDocCompletenessSection(allCollections);
        if (docCompleteness.errorCount > 0 || docCompleteness.warningCount > 0) {
          lines.push(...docCompleteness.lines);
        }
      }

      const plannedWork = await queryPlannedWork();

      if (hasPlannedWork(plannedWork)) {
        lines.push("");
        lines.push(...buildPlannedWorkSection(plannedWork, priorSessions, recoveryBlock));
      } else if (recoveryBlock) {
        lines.push("");
        lines.push(...formatRecoveryBlock(recoveryBlock));
      }

      try {
        // Active-default (DEC-473) means returned rows are already active;
        // we only need seededByPlatform + collectionId to count committed work.
        // (No `collection` field exists server-side — schema stores collectionId
        // only — so the old `?? e.collection` fallback was always inert.)
        const allEntries = await kernelQuery<Array<{ seededByPlatform?: boolean; collectionId?: string }>>(
          "chain.listEntries",
          { fields: ["seededByPlatform", "collectionId"] },
        );
        const committed = (allEntries ?? []).filter((e) => !e.seededByPlatform);
        const committedCollections = new Set(committed.map((e) => e.collectionId));

        if (committed.length >= 10 && committedCollections.size >= 3) {
          lines.push("");
          lines.push(`_${committed.length} entries across ${committedCollections.size} collections on the Chain._`);
        }
      } catch { /* non-critical */ }

      lines.push("");
      lines.push(`Working on: **${task}**`);
      lines.push("");
    } else {
      if (activeBets.length > 0) {
        lines.push("");
        lines.push("## Active bets — current scope");
        lines.push("_Work outside these bets requires explicit user confirmation._");
        lines.push("");
        for (const e of activeBets) {
          lines.push(`- \`${e.entryId ?? e.name}\` ${e.name}`);
        }
        lines.push("");
      }

      lines.push(...buildOperatingProtocol());

      if (coherence) {
        lines.push(...coherence.lines);
      }

      if (allCollections.length > 0) {
        const docCompleteness = buildDocCompletenessSection(allCollections);
        if (docCompleteness.errorCount > 0 || docCompleteness.warningCount > 0) {
          lines.push(...docCompleteness.lines);
        }
      }

      if (priorSessions.length > 0 && !recoveryBlock) {
        const last = priorSessions[0];
        const date = last.startedAt ? new Date(last.startedAt).toISOString().split("T")[0] : "unknown";
        const created = Array.isArray(last.entriesCreated) ? last.entriesCreated.length : last.entriesCreated ?? 0;
        const modified = Array.isArray(last.entriesModified) ? last.entriesModified.length : last.entriesModified ?? 0;
        lines.push(`_Last session (${date}): ${created} created, ${modified} modified._`);
        lines.push("");
      }

      if (recoveryBlock) {
        lines.push("");
        lines.push(...formatRecoveryBlock(recoveryBlock));
      }

      lines.push("**What are you working on?** Tell me and I'll load relevant governance and context.");
      lines.push("");
    }
  }

  if (errors.length > 0) {
    lines.push("## Errors");
    for (const err of errors) lines.push(`- ${err}`);
    lines.push("");
  }

  const { oriented, orientationStatus } = await tryMarkOriented(agentSessionId, coherence?.snapshot);

  const stageLabel = stage ?? "active";
  const gapCount = readiness?.gaps?.length ?? 0;
  const summaryParts = [`Product Brain ready. Stage: ${stageLabel}.`];
  if (gapCount > 0) summaryParts.push(`${gapCount} gap${gapCount === 1 ? "" : "s"}.`);
  if (openTensions.length > 0) summaryParts.push(`${openTensions.length} open tension${openTensions.length === 1 ? "" : "s"}.`);

  const next: NextAction[] = [];
  if (gapCount > 0) {
    next.push({ tool: "capture", description: "Fill a gap", parameters: {} });
  }
  if (openTensions.length > 0) {
    next.push({ tool: "entries", description: "View open tensions", parameters: { action: "list", collection: "tensions" } });
  }

  return {
    text: lines.join("\n"),
    structuredContent: success(
      summaryParts.join(" "),
      {
        stage,
        readinessScore: readiness?.score ?? null,
        totalGaps: gapCount,
        openTensions: openTensions.length,
        oriented,
        orientationStatus,
        ...(agentSessionId ? { sessionId: agentSessionId } : {}),
      },
      next.length > 0 ? next : undefined,
    ),
  };
}

/**
 * WP-484 S2 (§3): extracted request handler — reused by the legacy `start_pb`
 * standalone tool AND the new `orient action=start` (the two call `start_pb`
 * absorbs into `orient`, §3 core-nouns table). One business-logic path.
 */
export async function handleStartPb({ task }: { task?: string }) {
  const errors: string[] = [];
  const agentSessionId = getAgentSessionId();

  let wsCtx: Awaited<ReturnType<typeof getWorkspaceContext>> | null = null;
  try {
    wsCtx = await getWorkspaceContext();
  } catch (e: unknown) {
    errors.push(`Workspace: ${e instanceof Error ? e.message : String(e)}`);
  }

  if (!wsCtx) {
    const text =
      "# Could not connect to Product Brain\n\n" +
      (errors.length > 0 ? errors.map((e) => `- ${e}`).join("\n") : "Check your API key and CONVEX_SITE_URL.");
    return {
      content: [{ type: "text" as const, text }],
      structuredContent: failure(
        "BACKEND_UNAVAILABLE",
        "Could not connect to Product Brain.",
        "Check your API key and CONVEX_SITE_URL.",
      ),
    };
  }

  // Branch on readiness stage (PAT-227).
  // Do NOT default to "blank" on readiness failure — an active workspace
  // seeing setup instructions on a transient error would be actively harmful.
  let stage: string | null = null;
  try {
    const readiness = await kernelQuery<WorkspaceReadiness>("chain.workspaceReadiness");
    stage = readiness?.stage ?? "blank";
  } catch {
    errors.push("Readiness check unavailable — showing workspace summary.");
  }

  if (stage === "blank" || stage === "seeded") {
    // DEC-993 / STD-235: stamp surface + return setup skill body verbatim
    const result = await buildSetupResponse(agentSessionId);
    return {
      content: textContent(result.text),
      structuredContent: result.structuredContent,
    };
  }

  // grounded, connected, or readiness failed → orient-style standup
  if (stage === "grounded" || stage === "connected") {
    void kernelMutation("chain.setOnboardingCompleted", {}).catch(() => { /* non-critical */ });
  }

  const orientResult = await buildOrientResponse(wsCtx, agentSessionId, errors, task);
  return {
    content: [{ type: "text" as const, text: orientResult.text }],
    structuredContent: orientResult.structuredContent,
  };
}

// WP-484 S3 (clean break, TEN-2157): the standalone `start_pb` tool retired
// here. handleStartPb stays live — orient.ts (the `orient` compound,
// action=start) is its only remaining caller.
